import { DeleteOutlined, EditOutlined, LockOutlined, MoreOutlined, PlusOutlined } from '@ant-design/icons'
import { Button, Dropdown, Flex, Popconfirm, Spin, Tooltip, Typography } from 'antd'
import clsx from 'clsx'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../auth'
import { CountChip } from '../../components/count-chip'
import { ListContainer } from '../../components/list-container'
import { DELETE_MESSAGE_TEMPLATE, PRIVATE_MESSAGE_TEMPLATE, UPDATE_MESSAGE_TEMPLATE } from '../../user/permission'
import { MessageTemplate, MessageTemplatePurpose, messageTemplatePurposes } from '../message-template'
import { useDeleteMessageTemplate } from '../message-template-hooks'
import { TemplateBubble } from './template-bubble'

type MessageTemplateListProps = {
    templates: MessageTemplate[]
    loading?: boolean
    // Off when the list is already narrowed to one purpose: a single heading
    // above a single group repeats the tab that was just picked.
    grouped?: boolean
    // Show purposes without templates too, so a missing one (say, no rejection
    // message yet) is visible. Off while filtering, where empty groups are noise.
    showEmptyGroups?: boolean
    onEdit: (template: MessageTemplate) => void
    onAdd?: (purpose: MessageTemplatePurpose) => void
}

// Templates are grouped by the moment a campaign sends them, which is how
// recruiters pick them: the opener, the nudge, the rejection.
export function MessageTemplateList({
    templates,
    loading,
    grouped = true,
    showEmptyGroups = false,
    onEdit,
    onAdd,
}: MessageTemplateListProps) {
    const { t } = useTranslation()
    const { can } = useAuth()
    const deleteTemplate = useDeleteMessageTemplate()

    const groups = messageTemplatePurposes
        .map((purpose) => ({
            purpose,
            templates: templates.filter((template) => template.purpose === purpose),
        }))
        .filter((group) => showEmptyGroups || group.templates.length > 0)

    if (!loading && groups.every((group) => group.templates.length === 0)) {
        return (
            <ListContainer
                isEmpty
                emptyText={t('No templates found')}
            />
        )
    }

    const rows = (templates: MessageTemplate[]) =>
        templates.map((template) => (
            <TemplateRow
                key={template.id}
                template={template}
                onEdit={onEdit}
                editable={can(UPDATE_MESSAGE_TEMPLATE)}
                removable={can(DELETE_MESSAGE_TEMPLATE)}
                showVisibility={can(PRIVATE_MESSAGE_TEMPLATE)}
                deleting={deleteTemplate.isPending && deleteTemplate.variables === template.id}
                onDelete={() => deleteTemplate.mutate(template.id)}
            />
        ))

    return (
        <Spin spinning={!!loading}>
            {grouped ? (
                <Flex
                    vertical
                    gap={28}
                >
                    {groups.map((group) => (
                        <TemplateGroup
                            key={group.purpose}
                            purpose={group.purpose}
                            count={group.templates.length}
                            onAdd={onAdd}
                        >
                            {rows(group.templates)}
                        </TemplateGroup>
                    ))}
                </Flex>
            ) : (
                <ListContainer>{rows(templates)}</ListContainer>
            )}
        </Spin>
    )
}

function TemplateGroup({
    purpose,
    count,
    onAdd,
    children,
}: {
    purpose: MessageTemplatePurpose
    count: number
    onAdd?: (purpose: MessageTemplatePurpose) => void
    children: React.ReactNode
}) {
    const { t } = useTranslation()

    // i18n-extract:
    // t('templatePurposeDescriptions.start')
    // t('templatePurposeDescriptions.recontact')
    // t('templatePurposeDescriptions.reject')
    return (
        <section>
            <Flex
                align="baseline"
                gap={8}
                wrap="wrap"
                className="mb-2.5 px-1"
            >
                <h3 className="m-0 text-[15px] font-medium text-ink">{t(`templatePurposes.${purpose}`)}</h3>
                <CountChip value={count} />
                <span className="text-xs text-ink-tertiary">{t(`templatePurposeDescriptions.${purpose}`)}</span>
            </Flex>

            {count > 0 ? (
                <ListContainer>{children}</ListContainer>
            ) : (
                <Flex
                    align="center"
                    justify="space-between"
                    gap="middle"
                    className="rounded-xl border border-dashed border-hairline-strong px-4 py-3 text-[13px] text-ink-tertiary"
                >
                    {t('No templates for this moment yet')}
                    {onAdd ? (
                        <Button
                            size="small"
                            type="link"
                            icon={<PlusOutlined />}
                            onClick={() => onAdd(purpose)}
                        >
                            {t('Add {{name}}', { name: t('Template') })}
                        </Button>
                    ) : null}
                </Flex>
            )}
        </section>
    )
}

const statusPill: Record<MessageTemplate['status'], string> = {
    APPROVED: 'bg-live/10 text-live-ink',
    PENDING: 'bg-held/10 text-held-ink',
    REJECTED: 'bg-rejected/10 text-rejected-ink',
}

const statusDot: Record<MessageTemplate['status'], string> = {
    APPROVED: 'bg-live',
    PENDING: 'bg-held',
    REJECTED: 'bg-rejected',
}

function TemplateStatusPill({ status }: { status: MessageTemplate['status'] }) {
    const { t } = useTranslation()

    return (
        <span
            className={clsx(
                'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium',
                statusPill[status]
            )}
        >
            <span className={clsx('size-1.5 rounded-full', statusDot[status])} />
            {t(`templateStatuses.${status}`)}
        </span>
    )
}

type TemplateRowProps = {
    template: MessageTemplate
    onEdit: (template: MessageTemplate) => void
    editable: boolean
    removable: boolean
    showVisibility: boolean
    deleting: boolean
    onDelete: () => void
}

function TemplateRow({ template, onEdit, editable, removable, showVisibility, deleting, onDelete }: TemplateRowProps) {
    const { t } = useTranslation()

    // i18n-extract:
    // t('templateCategories.MARKETING')
    // t('templateCategories.UTILITY')
    // t('templateCategories.AUTHENTICATION')
    const meta = [
        (template.language || '').toUpperCase(),
        t(`templateCategories.${template.category}`, { defaultValue: template.category }),
    ].filter(Boolean)

    return (
        <Flex
            vertical
            gap={10}
            className="border-b border-divider px-4 py-3.5 transition-colors last:border-b-0 hover:bg-surface-hover"
        >
            <Flex
                align="center"
                justify="space-between"
                gap="middle"
            >
                <Flex
                    align="baseline"
                    gap={10}
                    wrap="wrap"
                    className="min-w-0"
                >
                    <Typography.Text className="truncate text-sm font-medium">{template.title}</Typography.Text>
                    {showVisibility && !template.public ? (
                        <Tooltip title={t('Private')}>
                            <LockOutlined
                                className="text-xs text-ink-tertiary"
                                aria-label={t('Private')}
                            />
                        </Tooltip>
                    ) : null}
                    <span className="text-xs text-ink-tertiary">{meta.join(' · ')}</span>
                </Flex>

                <Flex
                    align="center"
                    gap={6}
                    className="shrink-0"
                >
                    <TemplateStatusPill status={template.status} />
                    <TemplateActions
                        editable={editable}
                        removable={removable}
                        deleting={deleting}
                        onEdit={() => onEdit(template)}
                        onDelete={onDelete}
                    />
                </Flex>
            </Flex>

            <TemplateBubble
                block
                body={template.body}
                className="text-[13px]"
            />

            {template.status === 'REJECTED' && template.rejectionReason ? (
                <div className="rounded-lg bg-rejected/10 px-[9px] py-1.5 text-xs leading-snug text-rejected-ink">
                    <span className="font-semibold">{t('Rejected by Meta')}:</span> {template.rejectionReason}
                </div>
            ) : null}
        </Flex>
    )
}

function TemplateActions({
    editable,
    removable,
    deleting,
    onEdit,
    onDelete,
}: {
    editable: boolean
    removable: boolean
    deleting: boolean
    onEdit: () => void
    onDelete: () => void
}) {
    const { t } = useTranslation()
    const [confirming, setConfirming] = useState(false)

    if (!editable && !removable) {
        return null
    }

    return (
        <Popconfirm
            open={confirming}
            onOpenChange={(open) => !open && setConfirming(false)}
            placement="bottomRight"
            title={t('Delete template?')}
            description={
                <Flex vertical>
                    <span>{t('This will delete the template from both Evaa and Meta.')}</span>
                    <span>{t('Templates linked to campaigns cannot be deleted.')}</span>
                </Flex>
            }
            okText={t('Delete')}
            cancelText={t('Cancel')}
            okButtonProps={{ danger: true }}
            onConfirm={() => {
                setConfirming(false)
                onDelete()
            }}
            onCancel={() => setConfirming(false)}
        >
            <Dropdown
                trigger={['click']}
                placement="bottomRight"
                menu={{
                    items: [
                        { key: 'edit', label: t('Edit'), icon: <EditOutlined />, disabled: !editable },
                        {
                            key: 'delete',
                            label: t('Delete'),
                            icon: <DeleteOutlined />,
                            danger: true,
                            disabled: !removable,
                        },
                    ],
                    onClick: ({ key }) => (key === 'edit' ? onEdit() : setConfirming(true)),
                }}
            >
                <Button
                    size="small"
                    type="text"
                    icon={<MoreOutlined />}
                    loading={deleting}
                    className="text-ink-tertiary"
                    aria-label={t('More')}
                />
            </Dropdown>
        </Popconfirm>
    )
}
